import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Send } from 'lucide-react';
import { User, Message, Conversation } from '../types';
import { subscribeToChatEvents } from '../utils/chatEvents';
import { getApiBase } from '../utils/apiBase';

interface ChatModalProps {
  customer: User;
  token: string;
  onClose: () => void;
}

interface StoredUser { _id: string; name: string; email: string; role: string; }

const ChatModal: React.FC<ChatModalProps> = ({ customer, token, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [previousMessages, setPreviousMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const API_BASE = getApiBase();
  const api = axios.create({
    baseURL: `${API_BASE}/api/chat`,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  useEffect(() => {
    const stored = localStorage.getItem('authUser');
    if (!stored) { onClose(); return; }
    try { setCurrentUser(JSON.parse(stored)); } catch { onClose(); }
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' }), 0);
  };
  useEffect(scrollToBottom, [messages, previousMessages]);

  const initConversation = async () => {
    if (!currentUser) return;
    setConversation(null); setLoading(true); setMessages([]); setPreviousMessages([]);
    try {
      const payload: any = { customerId: customer._id };
      if (currentUser.role === 'admin' || currentUser.role === 'manager') payload.adminId = currentUser._id;
      const convRes = await api.post('/conversations', payload);
      let conv = convRes.data.data || convRes.data;
      if (conv?.status === 'closed') {
        const newConvRes = await api.post('/conversations', { ...payload, forceNew: true });
        conv = newConvRes.data.data || newConvRes.data;
      }
      setConversation(conv);
      const msgRes = await api.get(`/conversations/${conv._id}/messages`);
      setMessages(msgRes.data.data || msgRes.data.messages || []);
      if (conv.previousConversationId) {
        const prevRes = await api.get(`/conversations/${conv.previousConversationId}/messages`);
        setPreviousMessages(prevRes.data.data || prevRes.data.messages || []);
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { if (currentUser) initConversation(); }, [currentUser]);

  useEffect(() => {
    if (!conversation) return;
    const loadMessages = async () => {
      try {
        const res = await api.get(`/conversations/${conversation._id}/messages`);
        setMessages(res.data.data || res.data.messages || []);
      } catch (err) {
        console.error(err);
      }
    };
    const subscription = subscribeToChatEvents(API_BASE, token, event => {
      if (
        (event.type === 'chat.message.created' || event.type === 'chat.unread.changed') &&
        event.data?.conversationId === conversation._id
      ) {
        loadMessages();
      }
    });
    return () => subscription.close();
  }, [conversation]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation || !currentUser) return;
    try {
      setSending(true);
      const res = await api.post('/messages', { conversationId: conversation._id, content: newMessage.trim(), senderId: currentUser._id, senderRole: currentUser.role });
      setMessages(prev => [...prev, res.data.data || res.data.message]);
      setNewMessage('');
    } catch (err) { console.error(err); } finally { setSending(false); }
  };

  useEffect(() => {
    if (!conversation || !currentUser || messages.length === 0) return;
    api.patch(`/conversations/${conversation._id}/mark-read`).catch(() => {});
  }, [messages.length, conversation, currentUser]);

  const handleClose = async () => {
    if (!conversation) return;
    await api.patch(`/conversations/${conversation._id}/close`);
    setConversation(prev => prev && { ...prev, status: 'closed' });
  };

  const formatTime = (d: string) => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (!currentUser) return null;

  const initials = (name: string) => (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      <div style={{ background: '#fff', width: '100%', maxWidth: '560px', height: '85vh', borderRadius: '20px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#fff5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, color: '#F1561D', flexShrink: 0 }}>
              {initials(customer.name)}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>{customer.name}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>{customer.email}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {conversation?.status === 'open' && (
              <button onClick={handleClose}
                style={{ fontSize: '13px', color: '#dc2626', background: '#fff5f5', border: '1px solid #fee2e2', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', fontWeight: 500 }}>
                Close Chat
              </button>
            )}
            <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6b7280' }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Messages ────────────────────────────────────────────────── */}
        <div ref={containerRef} style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f8f7f5', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: '40px', fontSize: '14px' }}>Loading chat…</div>
          ) : (
            <>
              {previousMessages.length > 0 && (
                <div style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '8px 0', borderBottom: '1px dashed #e5e7eb', marginBottom: '8px' }}>
                  Previous conversation
                </div>
              )}
              {[...previousMessages, ...messages].map((msg, idx) => {
                const mine = msg.senderId === currentUser._id;
                return (
                  <div key={msg._id ?? idx} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '72%', padding: '10px 14px', borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: mine ? '#F1561D' : '#fff', color: mine ? '#fff' : '#111827',
                      fontSize: '14px', lineHeight: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}>
                      <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                      <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.65, textAlign: mine ? 'right' : 'left' }}>
                        {formatTime(msg.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* ── Input ───────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6', flexShrink: 0, background: '#fff' }}>
          {conversation?.status === 'closed' ? (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#dc2626' }}>Conversation closed</p>
              <button onClick={initConversation}
                style={{ background: '#F1561D', color: '#fff', border: 'none', borderRadius: '20px', padding: '8px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                Start New Chat
              </button>
            </div>
          ) : (
            <form onSubmit={handleSend} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="Type a message…"
                disabled={loading}
                style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '24px', padding: '10px 16px', fontSize: '14px', outline: 'none', background: '#f8f7f5' }}
              />
              <button type="submit" disabled={!newMessage.trim() || sending}
                style={{ background: '#F1561D', color: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, opacity: (!newMessage.trim() || sending) ? 0.5 : 1 }}>
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
